/**
 * Service Afterlife Page — SRV-FG-4
 * Unified page for abandoned equipment, compensation, afterlife decisions,
 * batch worksheet creation, and voluntary surrender.
 */

import {
  type AbandonedEquipmentItem,
  type AfterlifeEquipmentItem,
  batchCreateWorksheets,
  getAbandonedEquipment,
  getAfterlifeDecisions,
  recordCompensationPayment,
  recordVoluntarySurrender,
} from '@/api/service-afterlife';
import { Button, Card, Input } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ============================================
// Tab definitions
// ============================================

type TabId = 'abandoned' | 'afterlife' | 'batch' | 'compensation' | 'surrender';

// ============================================
// Abandoned Equipment Tab (SRV-FG-4-1)
// ============================================

function AbandonedEquipmentTab() {
  const { t } = useTranslation();
  const [items, setItems] = useState<AbandonedEquipmentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAbandonedEquipment(1, 50);
      setItems(response.data);
      setTotal(response.meta.total);
    } catch (err) {
      console.error('Failed to load abandoned equipment:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>;
  }

  if (items.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-lg font-medium text-foreground">{t('service.abandoned.noItems')}</p>
        <p className="mt-2 text-sm text-muted-foreground">{t('service.abandoned.noItemsDesc')}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('service.abandoned.totalCount', { count: String(total) })}
        </p>
      </div>
      <div className="space-y-3">
        {items.map(item => (
          <Card key={item.rentalId} className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-foreground">{item.equipmentName}</h3>
                {item.equipmentSerialNumber && (
                  <p className="text-sm text-muted-foreground">SN: {item.equipmentSerialNumber}</p>
                )}
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('service.abandoned.partner')}: {item.partnerName}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('service.abandoned.rental')}: {item.rentalNumber}
                </p>
              </div>
              <div className="text-right">
                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/50 dark:text-red-200">
                  {item.daysSinceEnd} {t('service.abandoned.daysOverdue')}
                </span>
                <p className="mt-2 text-lg font-bold text-red-600 dark:text-red-400">
                  {item.estimatedStorageFee.toLocaleString('hu-HU')} Ft
                </p>
                <p className="text-xs text-muted-foreground">
                  {item.dailyRate} Ft/{t('service.abandoned.perDay')}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Afterlife Decisions Tab (SRV-FG-4-3)
// ============================================

function AfterlifeDecisionsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scrap, setScrap] = useState<AfterlifeEquipmentItem[]>([]);
  const [repair, setRepair] = useState<AfterlifeEquipmentItem[]>([]);
  const [decision, setDecision] = useState<AfterlifeEquipmentItem[]>([]);
  const [summary, setSummary] = useState({
    scrapCount: 0,
    repairCount: 0,
    decisionCount: 0,
    total: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAfterlifeDecisions();
      setScrap(response.data.needsScrap);
      setRepair(response.data.needsRepair);
      setDecision(response.data.needsDecision);
      setSummary(response.data.summary);
    } catch (err) {
      console.error('Failed to load afterlife decisions:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>;
  }

  const renderColumn = (
    title: string,
    items: AfterlifeEquipmentItem[],
    colorClass: string,
    badgeBg: string
  ) => (
    <div className="flex-1">
      <h3 className={cn('mb-3 text-lg font-semibold', colorClass)}>
        {title} ({items.length})
      </h3>
      <div className="space-y-3">
        {items.length === 0 ? (
          <Card className="p-4 text-center text-sm text-muted-foreground">
            {t('service.afterlife.noItems')}
          </Card>
        ) : (
          items.map(item => (
            <Card
              key={item.id}
              className="cursor-pointer p-4 transition-colors hover:bg-muted/50"
              onClick={() => item.worksheetId && navigate(`/worksheet/${item.worksheetId}`)}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-foreground">{item.name}</p>
                  {item.serialNumber && (
                    <p className="text-xs text-muted-foreground">SN: {item.serialNumber}</p>
                  )}
                </div>
                <span
                  className={cn(
                    'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                    badgeBg
                  )}
                >
                  {item.conditionScore}/10
                </span>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                <span>
                  {t('service.afterlife.repairs')}: {item.repairCount}
                </span>
                <span>
                  {t('service.afterlife.age')}: {item.ageMonths} {t('service.afterlife.months')}
                </span>
                <span>
                  {t('service.afterlife.condition')}: {item.conditionScore}/10
                </span>
              </div>
              <p className="mt-2 text-xs italic text-muted-foreground">{item.suggestedAction}</p>
            </Card>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Summary stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">{t('service.afterlife.scrapTitle')}</p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{summary.scrapCount}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">{t('service.afterlife.repairTitle')}</p>
          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
            {summary.repairCount}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">{t('service.afterlife.decisionTitle')}</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {summary.decisionCount}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">{t('common.total')}</p>
          <p className="text-2xl font-bold text-foreground">{summary.total}</p>
        </Card>
      </div>

      {/* Three columns */}
      <div className="flex flex-col gap-6 lg:flex-row">
        {renderColumn(
          t('service.afterlife.scrapTitle'),
          scrap,
          'text-red-600 dark:text-red-400',
          'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200'
        )}
        {renderColumn(
          t('service.afterlife.repairTitle'),
          repair,
          'text-amber-600 dark:text-amber-400',
          'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200'
        )}
        {renderColumn(
          t('service.afterlife.decisionTitle'),
          decision,
          'text-blue-600 dark:text-blue-400',
          'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
        )}
      </div>
    </div>
  );
}

// ============================================
// Batch Worksheet Tab (SRV-FG-4-4)
// ============================================

function BatchWorksheetTab() {
  const { t } = useTranslation();
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodes, setBarcodes] = useState<string[]>([]);
  const [worksheetType, setWorksheetType] = useState<
    'FIZETOS' | 'GARANCIALIS' | 'BERLESI' | 'KARBANTARTAS'
  >('FIZETOS');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{
    created: Array<{ barcode: string; worksheetId: string; worksheetNumber: string }>;
    failures: Array<{ barcode: string; reason: string }>;
  } | null>(null);

  const addBarcode = () => {
    const trimmed = barcodeInput.trim();
    if (trimmed && !barcodes.includes(trimmed)) {
      setBarcodes(prev => [...prev, trimmed]);
      setBarcodeInput('');
    }
  };

  const removeBarcode = (index: number) => {
    setBarcodes(prev => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addBarcode();
    }
  };

  const handleSubmit = async () => {
    if (barcodes.length === 0) return;
    setIsSubmitting(true);
    setResult(null);
    try {
      const response = await batchCreateWorksheets({
        equipmentBarcodes: barcodes,
        type: worksheetType,
      });
      setResult(response.data);
      // Remove successfully created barcodes from the list
      const createdBarcodes = new Set(response.data.created.map(c => c.barcode));
      setBarcodes(prev => prev.filter(b => !createdBarcodes.has(b)));
    } catch (err) {
      console.error('Batch creation failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Scanner input */}
      <Card className="p-6">
        <h3 className="mb-4 text-lg font-semibold text-foreground">
          {t('worksheet.batch.scanTitle')}
        </h3>
        <div className="flex gap-2">
          <Input
            value={barcodeInput}
            onChange={e => setBarcodeInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('worksheet.batch.scanPlaceholder')}
            className="flex-1"
            autoFocus
          />
          <Button onClick={addBarcode} variant="outline">
            {t('common.add')}
          </Button>
        </div>

        {/* Worksheet type selector */}
        <div className="mt-4">
          <label className="mb-1 block text-sm font-medium text-muted-foreground">
            {t('worksheet.batch.worksheetType')}
          </label>
          <select
            value={worksheetType}
            onChange={e => setWorksheetType(e.target.value as typeof worksheetType)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="FIZETOS">{t('worksheet.type.fizetos')}</option>
            <option value="GARANCIALIS">{t('worksheet.type.garancialis')}</option>
            <option value="BERLESI">{t('worksheet.type.berlesi')}</option>
            <option value="KARBANTARTAS">{t('worksheet.type.karbantartas')}</option>
          </select>
        </div>
      </Card>

      {/* Barcode list */}
      {barcodes.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">
              {t('worksheet.batch.scannedItems')} ({barcodes.length})
            </h3>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('worksheet.batch.createAll')}
            </Button>
          </div>
          <div className="mt-4 space-y-2">
            {barcodes.map((barcode, idx) => (
              <div
                key={`${barcode}-${idx}`}
                className="flex items-center justify-between rounded-md border border-border px-3 py-2"
              >
                <span className="font-mono text-sm text-foreground">{barcode}</span>
                <button
                  onClick={() => removeBarcode(idx)}
                  className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  {t('common.remove')}
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Results */}
      {result && (
        <Card className="p-6">
          <h3 className="mb-4 text-lg font-semibold text-foreground">
            {t('worksheet.batch.results')}
          </h3>
          {result.created.length > 0 && (
            <div className="mb-4">
              <h4 className="mb-2 text-sm font-medium text-green-600 dark:text-green-400">
                {t('worksheet.batch.successCount', { count: String(result.created.length) })}
              </h4>
              <div className="space-y-1">
                {result.created.map(item => (
                  <div key={item.worksheetId} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-muted-foreground">{item.barcode}</span>
                    <span className="text-green-600 dark:text-green-400">
                      {item.worksheetNumber}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.failures.length > 0 && (
            <div>
              <h4 className="mb-2 text-sm font-medium text-red-600 dark:text-red-400">
                {t('worksheet.batch.failureCount', { count: String(result.failures.length) })}
              </h4>
              <div className="space-y-1">
                {result.failures.map((item, idx) => (
                  <div key={`fail-${idx}`} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-muted-foreground">{item.barcode}</span>
                    <span className="text-red-600 dark:text-red-400">{item.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ============================================
// Compensation Payment Tab (SRV-FG-4-2)
// ============================================

function CompensationPaymentTab() {
  const { t } = useTranslation();
  const [rentalId, setRentalId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [action, setAction] = useState<'WRITTEN_OFF' | 'TRANSFERRED_TO_INVENTORY'>('WRITTEN_OFF');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);

    const amountNum = parseFloat(amount);
    if (!rentalId.trim() || isNaN(amountNum) || amountNum <= 0 || !reason.trim()) {
      setErrorMessage(t('service.compensation.validationError'));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await recordCompensationPayment({
        rentalId: rentalId.trim(),
        amount: amountNum,
        reason: reason.trim(),
        action,
      });
      setSuccessMessage(response.data.message);
      setRentalId('');
      setAmount('');
      setReason('');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="mx-auto max-w-2xl p-6">
      <h3 className="mb-4 text-lg font-semibold text-foreground">
        {t('service.compensation.title')}
      </h3>
      <p className="mb-6 text-sm text-muted-foreground">{t('service.compensation.description')}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-muted-foreground">
            {t('service.compensation.rentalId')}
          </label>
          <Input
            value={rentalId}
            onChange={e => setRentalId(e.target.value)}
            placeholder={t('service.compensation.rentalIdPlaceholder')}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-muted-foreground">
            {t('service.compensation.amount')}
          </label>
          <Input
            type="number"
            min="0"
            step="1"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="pl. 150000"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-muted-foreground">
            {t('service.compensation.reason')}
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={t('service.compensation.reasonPlaceholder')}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
            rows={3}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-muted-foreground">
            {t('service.compensation.action')}
          </label>
          <select
            value={action}
            onChange={e => setAction(e.target.value as typeof action)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="WRITTEN_OFF">{t('service.compensation.writtenOff')}</option>
            <option value="TRANSFERRED_TO_INVENTORY">
              {t('service.compensation.transferredToInventory')}
            </option>
          </select>
        </div>

        {successMessage && (
          <div className="rounded-md bg-green-100 p-3 text-sm text-green-800 dark:bg-green-900/50 dark:text-green-200">
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="rounded-md bg-red-100 p-3 text-sm text-red-800 dark:bg-red-900/50 dark:text-red-200">
            {errorMessage}
          </div>
        )}

        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? t('common.loading') : t('service.compensation.submit')}
        </Button>
      </form>
    </Card>
  );
}

// ============================================
// Voluntary Surrender Tab (SRV-FG-4-5)
// ============================================

function VoluntarySurrenderTab() {
  const { t } = useTranslation();
  const [rentalId, setRentalId] = useState('');
  const [statement, setStatement] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);

    if (!rentalId.trim() || statement.trim().length < 5) {
      setErrorMessage(t('service.surrender.validationError'));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await recordVoluntarySurrender({
        rentalId: rentalId.trim(),
        statement: statement.trim(),
      });
      setSuccessMessage(response.data.message);
      setRentalId('');
      setStatement('');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="mx-auto max-w-2xl p-6">
      <h3 className="mb-4 text-lg font-semibold text-foreground">{t('service.surrender.title')}</h3>
      <p className="mb-6 text-sm text-muted-foreground">{t('service.surrender.description')}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-muted-foreground">
            {t('service.surrender.rentalId')}
          </label>
          <Input
            value={rentalId}
            onChange={e => setRentalId(e.target.value)}
            placeholder={t('service.surrender.rentalIdPlaceholder')}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-muted-foreground">
            {t('service.surrender.statement')}
          </label>
          <textarea
            value={statement}
            onChange={e => setStatement(e.target.value)}
            placeholder={t('service.surrender.statementPlaceholder')}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
            rows={4}
          />
        </div>

        {successMessage && (
          <div className="rounded-md bg-green-100 p-3 text-sm text-green-800 dark:bg-green-900/50 dark:text-green-200">
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="rounded-md bg-red-100 p-3 text-sm text-red-800 dark:bg-red-900/50 dark:text-red-200">
            {errorMessage}
          </div>
        )}

        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? t('common.loading') : t('service.surrender.submit')}
        </Button>
      </form>
    </Card>
  );
}

// ============================================
// Main Page
// ============================================

export function ServiceAfterlifePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('abandoned');

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: 'abandoned', label: t('service.abandoned.tabTitle') },
    { id: 'afterlife', label: t('service.afterlife.tabTitle') },
    { id: 'batch', label: t('worksheet.batch.tabTitle') },
    { id: 'compensation', label: t('service.compensation.tabTitle') },
    { id: 'surrender', label: t('service.surrender.tabTitle') },
  ];

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/worksheet')}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-foreground">
                {t('service.afterlife.pageTitle')}
              </h1>
              <p className="text-sm text-muted-foreground">{t('service.afterlife.pageSubtitle')}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Tabs */}
        <div className="mb-6 flex flex-wrap gap-2 border-b border-border pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'rounded-t-lg px-4 py-2 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'abandoned' && <AbandonedEquipmentTab />}
        {activeTab === 'afterlife' && <AfterlifeDecisionsTab />}
        {activeTab === 'batch' && <BatchWorksheetTab />}
        {activeTab === 'compensation' && <CompensationPaymentTab />}
        {activeTab === 'surrender' && <VoluntarySurrenderTab />}
      </main>
    </div>
  );
}
